import PodcastAPIRequestView from "issara-sdk/apis/PodcastAPIRequestViewM";

import {
  ActionHandler,
  BaseSeqState,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

export const ACTIONS = {
  GET_HISTORY: "GET_HISTORY",
  LOAD_IDEA: "LOAD_IDEA",
  GENERATE: "GENERATE",
  REFINE_THINK: "REFINE_THINK",
  REFINE_ARTICLE: "REFINE_ARTICLE",
  NEW_SCRIPT: "NEW_SCRIPT",
} as const;

export const SEQUENCE_NAME = "MeScript" as const;
export const MAIN_CARD_NAME = "CardMeScriptMain" as const;

/** ============== Sequence State ==============  */
export type MeScriptSequenceType = Partial<{
  sequenceIndex: "Action" | "START" | null;

  userProfile: UserProfile;
  historyList: HistoryItem[];
  selectedThreadId: string | null;
  ideaState: IdeaState;

  currentThinkIdx: number;
  currentArticleIdx: number;

  isLoading: boolean;
  isGenerating: boolean;
  isRefiningThink: boolean;
  isRefiningArticle: boolean;
}> | null;

export type State = Partial<{
  MeScriptSequence: MeScriptSequenceType;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "loadingStatus"
>;

/** ============== API Types ==============  */

export type HistoryItem = {
  thread_id: string;
  idea: string;
  updated_at: string;
  think_count: number;
  article_count: number;
};

type HistoryResponse = {
  status: string;
  history: HistoryItem[];
};

export type IdeaState = {
  idea: string;
  thread_id: string;
  think_versions: string[];
  article_versions: string[];
};

type LoadResponse = {
  status: string;
  state: IdeaState;
};

type GenerateResponse = {
  status: string;
  thread_id: string;
  state: IdeaState;
};

type RefineResponse = {
  status: string;
  state: IdeaState;
};

export type UserProfile = {
  email: string;
  image: string | null;
  user_id: number;
  first_name: string;
  last_name: string;
};

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.GET_HISTORY]: {};
  [ACTIONS.LOAD_IDEA]: { threadId: string };
  [ACTIONS.GENERATE]: { idea: string };
  [ACTIONS.REFINE_THINK]: {
    threadId: string;
    currentText: string;
    feedback: string;
  };
  [ACTIONS.REFINE_ARTICLE]: {
    threadId: string;
    currentArticle: string;
    deepThinkText: string;
    feedback: string;
  };
  [ACTIONS.NEW_SCRIPT]: {};
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ============== Initial State ==============  */
export const StateInitial: State = {
  MeScriptSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ============== Start ==============  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const apiToken = controller.apiToken;

  const [[profile], [history, historyErr]] = await Promise.all([
    PodcastAPIRequestView.get<UserProfile>({ endpoint: `/get_user_profile`, apiToken }),
    PodcastAPIRequestView.get<HistoryResponse>({ endpoint: `/api/idea2script/history`, apiToken }),
  ]);

  if (historyErr) {
    const state = controller.getState();
    controller.setState({
      errorMessage: {
        ...state.errorMessage,
        [MAIN_CARD_NAME]: historyErr,
      },
    });
  }

  controller.setState({
    MeScriptSequence: {
      sequenceIndex: "Action",
      userProfile: profile ?? undefined,
      historyList: history?.history ?? [],
      selectedThreadId: null,
      ideaState: undefined,
      currentThinkIdx: -1,
      currentArticleIdx: -1,
      isLoading: false,
      isGenerating: false,
      isRefiningThink: false,
      isRefiningArticle: false,
    },
  });
};

/** ============== Action ==============  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{
    [K in ActionType["action"]]: Handler<Params<K>>;
  }> = {
    [ACTIONS.GET_HISTORY]: HandleGetHistory,
    [ACTIONS.LOAD_IDEA]: HandleLoadIdea,
    [ACTIONS.GENERATE]: HandleGenerate,
    [ACTIONS.REFINE_THINK]: HandleRefineThink,
    [ACTIONS.REFINE_ARTICLE]: HandleRefineArticle,
    [ACTIONS.NEW_SCRIPT]: HandleNewScript,
  };

  const { action } = params;
  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  }
};

/** ============== Handlers ==============  */

const HandleGetHistory: Handler<Params<"GET_HISTORY">> = async (controller) => {
  const [data, err] = await PodcastAPIRequestView.get<HistoryResponse>({
    endpoint: `/api/idea2script/history`,
    apiToken: controller.apiToken,
  });

  if (err) return;

  const state = controller.getState();
  controller.setState({
    MeScriptSequence: {
      ...state.MeScriptSequence,
      historyList: data?.history ?? [],
    },
  });
};

const HandleLoadIdea: Handler<Params<"LOAD_IDEA">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    MeScriptSequence: {
      ...state.MeScriptSequence,
      selectedThreadId: params.threadId,
      isLoading: true,
    },
  });

  const [data, err] = await PodcastAPIRequestView.get<LoadResponse>({
    endpoint: `/api/idea2script/load/${params.threadId}`,
    apiToken: controller.apiToken,
  });

  const updatedState = controller.getState();

  if (err) {
    controller.setState({
      MeScriptSequence: { ...updatedState.MeScriptSequence, isLoading: false },
      errorMessage: { ...updatedState.errorMessage, [MAIN_CARD_NAME]: err },
    });
    return;
  }

  const ideaState = data!.state;
  controller.setState({
    MeScriptSequence: {
      ...updatedState.MeScriptSequence,
      ideaState,
      currentThinkIdx: (ideaState.think_versions?.length ?? 1) - 1,
      currentArticleIdx: (ideaState.article_versions?.length ?? 1) - 1,
      isLoading: false,
    },
  });
};

const HandleGenerate: Handler<Params<"GENERATE">> = async (controller, params) => {
  const state = controller.getState();

  if (!params.idea?.trim()) {
    controller.setState({
      errorMessage: {
        ...state.errorMessage,
        [MAIN_CARD_NAME]: "Please enter an idea to generate a script.",
      },
    });
    return;
  }

  controller.setState({
    MeScriptSequence: { ...state.MeScriptSequence, isGenerating: true },
  });

  const [data, err] = await PodcastAPIRequestView.post<GenerateResponse>({
    endpoint: `/api/idea2script/generate`,
    data: { idea: params.idea },
    apiToken: controller.apiToken,
  });

  const updatedState = controller.getState();

  if (err) {
    controller.setState({
      MeScriptSequence: { ...updatedState.MeScriptSequence, isGenerating: false },
      errorMessage: { ...updatedState.errorMessage, [MAIN_CARD_NAME]: err },
    });
    return;
  }

  const ideaState = data!.state;
  controller.setState({
    MeScriptSequence: {
      ...updatedState.MeScriptSequence,
      ideaState,
      selectedThreadId: data!.thread_id,
      currentThinkIdx: 0,
      currentArticleIdx: 0,
      isGenerating: false,
    },
  });

  HandleGetHistory(controller, {
    ...params,
    action: ACTIONS.GET_HISTORY,
  });
};

const HandleRefineThink: Handler<Params<"REFINE_THINK">> = async (controller, params) => {
  const state = controller.getState();

  if (!params.feedback?.trim() || !params.threadId) return;

  controller.setState({
    MeScriptSequence: { ...state.MeScriptSequence, isRefiningThink: true },
  });

  const [data, err] = await PodcastAPIRequestView.post<RefineResponse>({
    endpoint: `/api/idea2script/refine-think`,
    data: {
      thread_id: params.threadId,
      current_text: params.currentText,
      feedback: params.feedback,
    },
    apiToken: controller.apiToken,
  });

  const updatedState = controller.getState();

  if (err) {
    controller.setState({
      MeScriptSequence: { ...updatedState.MeScriptSequence, isRefiningThink: false },
      errorMessage: { ...updatedState.errorMessage, [MAIN_CARD_NAME]: err },
    });
    return;
  }

  const ideaState = data!.state;
  controller.setState({
    MeScriptSequence: {
      ...updatedState.MeScriptSequence,
      ideaState,
      currentThinkIdx: (ideaState.think_versions?.length ?? 1) - 1,
      isRefiningThink: false,
    },
  });

  HandleGetHistory(controller, {
    ...params,
    action: ACTIONS.GET_HISTORY,
  });
};

const HandleRefineArticle: Handler<Params<"REFINE_ARTICLE">> = async (controller, params) => {
  const state = controller.getState();

  if (!params.feedback?.trim() || !params.threadId) return;

  controller.setState({
    MeScriptSequence: { ...state.MeScriptSequence, isRefiningArticle: true },
  });

  const [data, err] = await PodcastAPIRequestView.post<RefineResponse>({
    endpoint: `/api/idea2script/refine-article`,
    data: {
      thread_id: params.threadId,
      current_article: params.currentArticle,
      deep_think_text: params.deepThinkText,
      feedback: params.feedback,
    },
    apiToken: controller.apiToken,
  });

  const updatedState = controller.getState();

  if (err) {
    controller.setState({
      MeScriptSequence: { ...updatedState.MeScriptSequence, isRefiningArticle: false },
      errorMessage: { ...updatedState.errorMessage, [MAIN_CARD_NAME]: err },
    });
    return;
  }

  const ideaState = data!.state;
  controller.setState({
    MeScriptSequence: {
      ...updatedState.MeScriptSequence,
      ideaState,
      currentArticleIdx: (ideaState.article_versions?.length ?? 1) - 1,
      isRefiningArticle: false,
    },
  });

  HandleGetHistory(controller, {
    ...params,
    action: ACTIONS.GET_HISTORY,
  });
};

const HandleNewScript: Handler<Params<"NEW_SCRIPT">> = async (controller) => {
  const state = controller.getState();

  controller.setState({
    MeScriptSequence: {
      ...state.MeScriptSequence,
      selectedThreadId: null,
      ideaState: undefined,
      currentThinkIdx: -1,
      currentArticleIdx: -1,
      isGenerating: false,
      isRefiningThink: false,
      isRefiningArticle: false,
    },
  });
};
